import { Ellipsoid } from "./geodesy.ellipsoid";
import { Observable } from "../events/events.observable";
import { Scalar } from "../math";
import { Cartesian3 } from "../geometry";
export var CartesianMode;
(function (CartesianMode) {
    CartesianMode[CartesianMode["ECEF"] = 0] = "ECEF";
    CartesianMode[CartesianMode["ENU"] = 1] = "ENU";
    CartesianMode[CartesianMode["NED"] = 2] = "NED";
})(CartesianMode || (CartesianMode = {}));
export class GeodeticSystem {
    static GetENUTransformMatrixFromFloat(lat, lon, alt = 0, ellipsoid = Ellipsoid.WGS84, rowOrder = true) {
        const lambda = lat * Scalar.DEG2RAD;
        const phi = lon * Scalar.DEG2RAD;
        const sin_lambda = Math.sin(lambda);
        const N = ellipsoid.semiMajorAxis / Math.sqrt(1 - ellipsoid.sqrEccentricity * sin_lambda * sin_lambda);
        const cos_lambda = Math.cos(lambda);
        const cos_phi = Math.cos(phi);
        const sin_phi = Math.sin(phi);
        const tmp = (alt + N) * cos_lambda;
        const x = tmp * cos_phi;
        const y = tmp * sin_phi;
        const z = (alt + ellipsoid.oneMinusSqrEccentricity * N) * sin_lambda;
        const om0 = -sin_phi;
        const om1 = -sin_lambda * cos_phi;
        const om2 = cos_lambda * cos_phi;
        const om4 = cos_phi;
        const om5 = -sin_lambda * sin_phi;
        const om6 = cos_lambda * sin_phi;
        const om9 = cos_lambda;
        const om10 = sin_lambda;
        const om12 = -x * om0 - y * om4;
        const om13 = -x * om1 - y * om5 - z * om9;
        const om14 = -x * om2 - y * om6 - z * om10;
        if (rowOrder) {
            return [om0, om1, om2, 0, om4, om5, om6, 0, 0, om9, om10, 0, om12, om13, om14, 1.0];
        }
        else {
            return [om0, om4, 0, om12, om1, om5, om9, om13, om2, om6, om10, om14, 0, 0, 0, 1.0];
        }
    }
    constructor(e, bounds) {
        this._ellipsoid = e || Ellipsoid.WGS84;
        this._bounds = bounds;
    }
    get ellipsoid() {
        return this._ellipsoid;
    }
    get ENUReference() {
        return this._enuReference;
    }
    set ENUReference(v) {
        if (this._enuReference) {
            if (this._enuReference.equals(v)) {
                return;
            }
            this._enuReference = v?.clone();
            this._enuTransform = undefined;
            if (this._enuObservable && this._enuObservable.hasObservers()) {
                this._enuObservable.notifyObservers(this);
            }
        }
    }
    get ENUTransform() {
        if (this._enuTransform === undefined && this._enuReference) {
            this._enuTransform = GeodeticSystem.GetENUTransformMatrixFromFloat(this._enuReference.lat, this._enuReference.lon, this._enuReference.alt, this._ellipsoid);
        }
        return this._enuTransform;
    }
    get ENUObservable() {
        this._enuObservable = this._enuObservable || new Observable();
        return this._enuObservable;
    }
    get cartesianMode() {
        return this._enuReference !== undefined ? CartesianMode.ENU : CartesianMode.ECEF;
    }
    geodeticFloatToCartesianToRef(lat, lon, alt, target, deg = true) {
        target = target || Cartesian3.Zero();
        let lambda = deg ? lat * Scalar.DEG2RAD : lat;
        let phi = deg ? lon * Scalar.DEG2RAD : lon;
        const sin_lambda = Math.sin(lambda);
        const cos_lambda = Math.cos(lambda);
        const cos_phi = Math.cos(phi);
        const sin_phi = Math.sin(phi);
        const N = this._ellipsoid._a / Math.sqrt(1.0 - this._ellipsoid._ee * sin_lambda * sin_lambda);
        const tmp = (alt + N) * cos_lambda;
        let x = tmp * cos_phi;
        let y = tmp * sin_phi;
        let z = (alt + this._ellipsoid._p1mee * N) * sin_lambda;
        if (this.ENUTransform) {
            const m = this.ENUTransform;
            const rx = x * m[0] + y * m[4] + z * m[8] + m[12];
            const ry = x * m[1] + y * m[5] + z * m[9] + m[13];
            const rz = x * m[2] + y * m[6] + z * m[10] + m[14];
            x = rx;
            y = ry;
            z = rz;
        }
        target.x = x;
        target.y = y;
        target.z = z;
        return target;
    }
    geodeticToCartesianToRef(geo, target) {
        return this.geodeticFloatToCartesianToRef(geo.lat, geo.lon, geo.alt || 0, target);
    }
}
GeodeticSystem.Default = new GeodeticSystem(Ellipsoid.WGS84);
//# sourceMappingURL=geodesy.system.js.map